import React, { useMemo } from "react";
import { BarChart3 } from "lucide-react";
import MasteryDonut from "./charts/MasteryDonut";
import QuizTrendLine from "./charts/QuizTrendLine";
import PerChapterMasteryBars from "./charts/PerChapterMasteryBars";

const KIND_COUNT = 5; // lesson / flashcards / quiz / scenario / summary

/**
 * Student-facing progress visualisation. Pure rendering over data the
 * ProgressContext already tracks — no LLM or API cost. Data derivation is
 * memoised; each chart is a lightweight sub-component.
 */
export default function StudentProgressCharts({ chapters, views, quizBest, quizHistory, lang = "en" }) {
  const isAr = lang === "ar";

  // Quiz-score timeline (oldest → newest) for the line chart.
  const timeline = useMemo(() => {
    if (!quizHistory?.length) return [];
    const sorted = [...quizHistory].sort(
      (a, b) => (a.completed_at || "").localeCompare(b.completed_at || ""),
    );
    return sorted.map((h, i) => ({
      idx: i + 1,
      pct: Math.round(h.percentage || 0),
      label: h.completed_at
        ? new Date(h.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : "",
      chapter: h.chapter_id,
    }));
  }, [quizHistory]);

  // Per-chapter reading + quiz % for the bar chart.
  const byChapter = useMemo(() => (chapters || []).map((ch) => {
    const uniqueKinds = new Set(
      views.filter((v) => v.chapter_id === ch.id).map((v) => v.kind),
    );
    const best = quizBest.find((b) => b.chapter_id === ch.id);
    return {
      id: ch.id,
      name: `Ch ${ch.number}`,
      title: ch.title,
      reading: Math.round((uniqueKinds.size / KIND_COUNT) * 100),
      quiz: best ? Math.round(best.percentage) : 0,
    };
  }), [chapters, views, quizBest]);

  // Overall mastery donut data.
  const mastery = useMemo(() => {
    if (!chapters?.length) return { pct: 0, parts: [] };
    const perCh = byChapter.map((c) => (c.reading * 0.5) + (c.quiz * 0.5));
    const overall = Math.round(perCh.reduce((s, n) => s + n, 0) / perCh.length);
    return {
      pct: overall,
      parts: [
        { name: isAr ? "أُتقن" : "Mastered", value: overall },
        { name: isAr ? "متبقٍّ" : "Remaining", value: 100 - overall },
      ],
    };
  }, [byChapter, chapters, isAr]);

  const hasAnyData = views.length > 0 || quizHistory.length > 0;

  if (!hasAnyData) {
    return (
      <div className="rounded-2xl bg-white border border-border p-8 text-center" data-testid="progress-charts-empty">
        <BarChart3 className="w-8 h-8 mx-auto text-sage mb-2" />
        <div className="font-display text-lg font-semibold">
          {isAr ? "ابدأ دراستك وستظهر رسوم تقدمك هنا" : "Start studying and your charts will appear here"}
        </div>
        <div className="text-sm text-muted-foreground mt-1">
          {isAr ? "اختر فصلاً أدناه وأكمل درسًا أو اختبارًا." : "Pick a chapter below and complete a lesson or quiz."}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-12" data-testid="progress-charts">
      <MasteryDonut pct={mastery.pct} parts={mastery.parts} isAr={isAr} />
      <QuizTrendLine timeline={timeline} isAr={isAr} />
      <PerChapterMasteryBars data={byChapter} isAr={isAr} />
    </div>
  );
}
